import { CarePhase, appliedHealthCareInflation } from '@shared';
import { computeMonthlyCostMetrics } from '@shared/calculations/funding/projection/sharedEligibility';

export interface PhaseInflationScheduleRow {
  // Calendar year the group's first care month falls in.
  careYear: number;
  // Care months in this group — rows sum to the phase's total care months.
  months: number;
  // Whole years of inflation applied to these months (the exponent).
  yearsOfInflation: number;
  multiplier: number;
}

// Exact schedule of the engine's annually-stepped inflation for one phase:
// walks the same per-month clock the claim-projection ledger prices with and
// groups consecutive care months sharing a whole-year inflation step. Because
// rows carry month counts, a sub-year phase straddling a step boundary reads
// "5 months ×4.36 / 3 months ×4.60" instead of implying multiple years of
// care, and the displayed phase factor is visibly the month-weighted average.
export function getPhaseInflationSchedule(
  client: Client,
  carePhase: CarePhase,
): PhaseInflationScheduleRow[] {
  const plan = client.projectionInputs?.phaseCarePlans?.find(p => p.carePhase === carePhase);
  if (!plan) return [];

  const phaseMonths = Math.round(plan.phaseCareMonthsNeeded);
  const inflationRate = appliedHealthCareInflation(client);
  const rows: PhaseInflationScheduleRow[] = [];
  for (let monthsIntoPhase = 0; monthsIntoPhase < phaseMonths; monthsIntoPhase++) {
    const { careCostInflationYears, projectedCareYear } = computeMonthlyCostMetrics(
      client,
      plan,
      monthsIntoPhase,
    );
    const lastRow = rows[rows.length - 1];
    if (lastRow && lastRow.yearsOfInflation === careCostInflationYears) {
      lastRow.months += 1;
    } else {
      rows.push({
        careYear: projectedCareYear,
        months: 1,
        yearsOfInflation: careCostInflationYears,
        multiplier: (1 + inflationRate) ** careCostInflationYears,
      });
    }
  }
  return rows;
}
